import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  Check,
  Mail,
  Phone,
  MapPin,
  Clock,
  Globe,
  CheckCircle,
  AlertCircle,
  X,
  Plus,
  Trash2,
  MessageSquare,
  HelpCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import Base from '../Base';

const ContactForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isDirty, setIsDirty] = useState(false);

  // Default form data matching the contact JSON structure
  const [formData, setFormData] = useState({
    hero: {
      title: 'Get In Touch',
      subtitle: 'Have a question or want to collaborate? We\'d love to hear from you.'
    },
    form: {
      title: 'Send Us a Message',
      successMessage: {
        title: 'Message Sent!',
        subtitle: 'Thank you for reaching out. We\'ll get back to you within 24 hours.'
      },
      fields: {
        name: { label: 'Your Name', placeholder: 'John Doe', required: true },
        email: { label: 'Email Address', placeholder: 'john@example.com', required: true },
        phone: { label: 'Phone Number', placeholder: '+91 98765 43210', required: false },
        subject: {
          label: 'Subject',
          required: true,
          options: [
            { value: '', label: 'Select a subject' },
            { value: 'general', label: 'General Inquiry' },
            { value: 'custom', label: 'Custom Design Request' },
            { value: 'order', label: 'Order Support' },
            { value: 'collaboration', label: 'Collaboration' }
          ]
        },
        message: { label: 'Message', placeholder: 'Tell us about your project or inquiry...', required: true, rows: 6 }
      },
      submitButton: { text: 'Send Message' }
    },
    contactInfo: {
      title: 'Contact Information'
    },
    businessHours: {
      title: 'Business Hours',
      hours: [
        { day: 'Monday - Friday', time: '9:00 AM - 6:00 PM' },
        { day: 'Saturday', time: '10:00 AM - 4:00 PM' },
        { day: 'Sunday', time: 'Closed' }
      ]
    },
    socialMedia: {
      title: 'Follow Us'
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        { question: 'Do you offer custom designs?', answer: 'Yes! We specialize in creating custom designs. Contact us to discuss your vision.' },
        { question: 'What\'s your turnaround time?', answer: 'Standard orders ship within 3-5 business days. Custom designs take 2-3 weeks.' },
        { question: 'Do you ship internationally?', answer: 'Currently, we ship within India. International shipping coming soon!' },
        { question: 'Can I visit your studio?', answer: 'Yes! Studio visits are by appointment only. Contact us to schedule.' }
      ]
    }
  });

  const updateFormData = (updater) => {
    setFormData(updater);
    setIsDirty(true);
  };

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/template/${templateId}/page/contact`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });

      // Load content from response - merge with defaults to handle missing fields
      if (response.data.content) {
        setFormData(prev => {
          const content = response.data.content;
          return {
            hero: { ...prev.hero, ...content.hero },
            form: {
              ...prev.form,
              ...content.form,
              successMessage: { ...prev.form.successMessage, ...content.form?.successMessage },
              fields: {
                ...prev.form.fields,
                ...content.form?.fields,
                name: { ...prev.form.fields.name, ...content.form?.fields?.name },
                email: { ...prev.form.fields.email, ...content.form?.fields?.email },
                phone: { ...prev.form.fields.phone, ...content.form?.fields?.phone },
                subject: { ...prev.form.fields.subject, ...content.form?.fields?.subject },
                message: { ...prev.form.fields.message, ...content.form?.fields?.message }
              },
              submitButton: { ...prev.form.submitButton, ...content.form?.submitButton }
            },
            contactInfo: { ...prev.contactInfo, ...content.contactInfo },
            businessHours: {
              ...prev.businessHours,
              ...content.businessHours,
              hours: content.businessHours?.hours || prev.businessHours.hours
            },
            socialMedia: { ...prev.socialMedia, ...content.socialMedia },
            faq: {
              ...prev.faq,
              ...content.faq,
              items: content.faq?.items || prev.faq.items
            }
          };
        });
      }
    } catch (error) {
      console.error('Error loading contact page data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (isDraft = true) => {
    // Basic validation on publish
    if (!isDraft) {
      if (!formData.hero?.title?.trim()) {
        setErrorMessage('Please enter a hero title before publishing.');
        setShowErrorModal(true);
        return;
      }
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.post(`${API_BASE_URL}/api/template/${templateId}/page/contact/save`, {
        status: isDraft ? 'draft' : 'complete',
        content: formData
      }, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      setIsDirty(false);
      setLastSaved(new Date());
      setShowSuccessModal(true);
    } catch (error) {
      if (error.response?.status === 401) { navigate('/login'); return; }
      setErrorMessage(error.response?.data?.message || 'Error saving changes. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  // Business Hours helpers
  const addBusinessHour = () => {
    setFormData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        hours: [...prev.businessHours.hours, { day: '', time: '' }]
      }
    }));
    setIsDirty(true);
  };

  const updateBusinessHour = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        hours: prev.businessHours.hours.map((h, i) =>
          i === index ? { ...h, [field]: value } : h
        )
      }
    }));
    setIsDirty(true);
  };

  const removeBusinessHour = (index) => {
    setFormData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        hours: prev.businessHours.hours.filter((_, i) => i !== index)
      }
    }));
    setIsDirty(true);
  };

  // FAQ helpers
  const addFaqItem = () => {
    setFormData(prev => ({
      ...prev,
      faq: {
        ...prev.faq,
        items: [...prev.faq.items, { question: '', answer: '' }]
      }
    }));
    setIsDirty(true);
  };

  const updateFaqItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      faq: {
        ...prev.faq,
        items: prev.faq.items.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
    setIsDirty(true);
  };

  const removeFaqItem = (index) => {
    setFormData(prev => ({
      ...prev,
      faq: {
        ...prev.faq,
        items: prev.faq.items.filter((_, i) => i !== index)
      }
    }));
    setIsDirty(true);
  };

  // Subject options helpers
  const addSubjectOption = () => {
    setFormData(prev => ({
      ...prev,
      form: {
        ...prev.form,
        fields: {
          ...prev.form.fields,
          subject: {
            ...prev.form.fields.subject,
            options: [...prev.form.fields.subject.options, { value: '', label: '' }]
          }
        }
      }
    }));
    setIsDirty(true);
  };

  const updateSubjectOption = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      form: {
        ...prev.form,
        fields: {
          ...prev.form.fields,
          subject: {
            ...prev.form.fields.subject,
            options: prev.form.fields.subject.options.map((opt, i) =>
              i === index ? { ...opt, [field]: value } : opt
            )
          }
        }
      }
    }));
    setIsDirty(true);
  };

  const removeSubjectOption = (index) => {
    setFormData(prev => ({
      ...prev,
      form: {
        ...prev.form,
        fields: {
          ...prev.form.fields,
          subject: {
            ...prev.form.fields.subject,
            options: prev.form.fields.subject.options.filter((_, i) => i !== index)
          }
        }
      }
    }));
    setIsDirty(true);
  };

  // Success Modal Component
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your contact page has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#D45614] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Loading contact page...</p>
          </div>
        </div>
      } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Contact Page</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-400">Saved {lastSaved.toLocaleTimeString()}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-1.5 bg-[#D45614] text-white text-sm font-medium rounded-lg hover:bg-[#c04d12] disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Hero Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <Mail className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Hero Section</h2>
              <p className="text-sm text-gray-500">The header of your contact page</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
              <input
                type="text"
                value={formData.hero.title}
                onChange={(e) => updateFormData(prev => ({ ...prev, hero: { ...prev.hero, title: e.target.value } }))}
                placeholder="Get In Touch"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Subtitle</label>
              <input
                type="text"
                value={formData.hero.subtitle}
                onChange={(e) => updateFormData(prev => ({ ...prev, hero: { ...prev.hero, subtitle: e.target.value } }))}
                placeholder="Have a question or want to collaborate?"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
          </div>
        </section>

        {/* Contact Form Settings */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Contact Form</h2>
              <p className="text-sm text-gray-500">Configure the contact form fields and messages</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Form Title</label>
              <input
                type="text"
                value={formData.form.title}
                onChange={(e) => updateFormData(prev => ({ ...prev, form: { ...prev.form, title: e.target.value } }))}
                placeholder="Send Us a Message"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Success Message Title</label>
                <input
                  type="text"
                  value={formData.form.successMessage.title}
                  onChange={(e) => updateFormData(prev => ({
                    ...prev,
                    form: { ...prev.form, successMessage: { ...prev.form.successMessage, title: e.target.value } }
                  }))}
                  placeholder="Message Sent!"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Success Message Subtitle</label>
                <input
                  type="text"
                  value={formData.form.successMessage.subtitle}
                  onChange={(e) => updateFormData(prev => ({
                    ...prev,
                    form: { ...prev.form, successMessage: { ...prev.form.successMessage, subtitle: e.target.value } }
                  }))}
                  placeholder="Thank you for reaching out."
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Submit Button Text</label>
              <input
                type="text"
                value={formData.form.submitButton.text}
                onChange={(e) => updateFormData(prev => ({
                  ...prev,
                  form: { ...prev.form, submitButton: { ...prev.form.submitButton, text: e.target.value } }
                }))}
                placeholder="Send Message"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>

            {/* Subject Options */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">Subject Options</label>
                <button
                  onClick={addSubjectOption}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#D45614] hover:bg-[#D45614]/5 rounded-lg transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Add Option
                </button>
              </div>
              <div className="space-y-2">
                {formData.form.fields.subject.options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option.value}
                      onChange={(e) => updateSubjectOption(index, 'value', e.target.value)}
                      placeholder="Value (e.g., general)"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => updateSubjectOption(index, 'label', e.target.value)}
                      placeholder="Label (e.g., General Inquiry)"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                    />
                    <button
                      onClick={() => removeSubjectOption(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section Titles */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <Phone className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Section Titles</h2>
              <p className="text-sm text-gray-500">Customize section headings</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Contact Info Section Title</label>
              <input
                type="text"
                value={formData.contactInfo.title}
                onChange={(e) => updateFormData(prev => ({ ...prev, contactInfo: { ...prev.contactInfo, title: e.target.value } }))}
                placeholder="Contact Information"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Social Media Section Title</label>
              <input
                type="text"
                value={formData.socialMedia.title}
                onChange={(e) => updateFormData(prev => ({ ...prev, socialMedia: { ...prev.socialMedia, title: e.target.value } }))}
                placeholder="Follow Us"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
          </div>
        </section>

        {/* Business Hours */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-[#D45614]" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Business Hours</h2>
                <p className="text-sm text-gray-500">Set your operating hours</p>
              </div>
            </div>
            <button
              onClick={addBusinessHour}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#D45614] bg-[#D45614]/5 hover:bg-[#D45614]/10 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Hours
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Section Title</label>
              <input
                type="text"
                value={formData.businessHours.title}
                onChange={(e) => updateFormData(prev => ({ ...prev, businessHours: { ...prev.businessHours, title: e.target.value } }))}
                placeholder="Business Hours"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>

            {formData.businessHours.hours.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No business hours added</p>
              </div>
            ) : (
              <div className="space-y-2">
                {formData.businessHours.hours.map((hour, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={hour.day}
                      onChange={(e) => updateBusinessHour(index, 'day', e.target.value)}
                      placeholder="Day(s) (e.g., Monday - Friday)"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={hour.time}
                      onChange={(e) => updateBusinessHour(index, 'time', e.target.value)}
                      placeholder="Time (e.g., 9:00 AM - 6:00 PM)"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                    />
                    <button
                      onClick={() => removeBusinessHour(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-[#D45614]" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Frequently Asked Questions</h2>
                <p className="text-sm text-gray-500">Add common questions and answers</p>
              </div>
            </div>
            <button
              onClick={addFaqItem}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#D45614] bg-[#D45614]/5 hover:bg-[#D45614]/10 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add FAQ
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Section Title</label>
              <input
                type="text"
                value={formData.faq.title}
                onChange={(e) => updateFormData(prev => ({ ...prev, faq: { ...prev.faq, title: e.target.value } }))}
                placeholder="Frequently Asked Questions"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>

            {formData.faq.items.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <HelpCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No FAQs added</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.faq.items.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-medium text-gray-500">FAQ #{index + 1}</span>
                      <button
                        onClick={() => removeFaqItem(index)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Question</label>
                        <input
                          type="text"
                          value={item.question}
                          onChange={(e) => updateFaqItem(index, 'question', e.target.value)}
                          placeholder="Enter the question..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Answer</label>
                        <textarea
                          value={item.answer}
                          onChange={(e) => updateFaqItem(index, 'answer', e.target.value)}
                          placeholder="Enter the answer..."
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none resize-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default ContactForm;
